/**
 * The clock-out readiness gate contract (ADR 0019).
 *
 * ONE POLICY, THREE SWITCHES. `ClockOutPolicy` is per org, optionally overridden per legal entity —
 * same resolution order as `WorkFormDefinition` (ADR 0009): the legal-entity row wins over the
 * org-default row, which wins over a built-in `enabled: false`. A tenant that has never configured
 * this behaves exactly as before punch-for-punch; nothing here changes behaviour until HR turns it on.
 *
 * EVERY UNMET CONDITION AT ONCE. `GET /attendance/clock-out-readiness` and the `out` punch's failure
 * body share one shape: a list of `reasons`, each with a machine `code` and a `resolutionAction` the
 * client can navigate to directly. The employee never gets a single blocker, fixes it, and discovers
 * a second one on the next attempt — the whole list is computed together, every time.
 *
 * Follows workform.contract.ts's conventions: DTOs distinct from the Prisma models, `routeMeta` on
 * every route. ROUTE ORDER MATTERS for any future `:param` route — see that file's header.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  dateOnlySchema,
  errorBodySchema,
  legalEntityIdSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
  instantSchema,
} from '../schemas/common.js';
import {
  CLOCK_OUT_BLOCK_REASON_CODES,
  CLOCK_OUT_RESOLUTION_ACTION_TYPES,
  CLOCK_OUT_OVERRIDE_STATUSES,
} from '../enums.js';

const c = initContract();

export const clockOutBlockReasonCodeSchema = z.enum(CLOCK_OUT_BLOCK_REASON_CODES);
export const clockOutResolutionActionTypeSchema = z.enum(CLOCK_OUT_RESOLUTION_ACTION_TYPES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const clockOutResolutionActionSchema = z.object({
  type: clockOutResolutionActionTypeSchema,
  /** A task id (`open_task`) or a learning assignment id (`open_learning_assignment`); absent for
   * `open_check_out_form`, which always resolves to the same screen. */
  refId: uuidSchema.optional(),
});

export const clockOutBlockReasonSchema = z.object({
  code: clockOutBlockReasonCodeSchema,
  message: z.string(),
  action: clockOutResolutionActionSchema,
});
export type ClockOutBlockReason = z.infer<typeof clockOutBlockReasonSchema>;

const clockOutReadinessSchemaShape = z.object({
  blocked: z.boolean(),
  reasons: z.array(clockOutBlockReasonSchema),
  /** A `pending` override request for this date already exists — the client shows "waiting for your
   * manager" instead of a "Request override" button. Absent when there is none. */
  pendingOverrideRequestId: uuidSchema.nullable().optional(),
});
export type ClockOutReadiness = z.infer<typeof clockOutReadinessSchemaShape>;
export const clockOutReadinessSchema: z.ZodType<ClockOutReadiness> = clockOutReadinessSchemaShape;

// ---------------------------------------------------------------------------
// Override / waiver requests (ADR 0020 §1)
// ---------------------------------------------------------------------------

export const clockOutOverrideStatusSchema = z.enum(CLOCK_OUT_OVERRIDE_STATUSES);

const clockOutOverrideRequestSchemaShape = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string().nullable(),
  date: dateOnlySchema,
  blockedReasons: z.array(clockOutBlockReasonSchema),
  employeeNote: z.string().nullable(),
  status: clockOutOverrideStatusSchema,
  decidedByPersonId: uuidSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: instantSchema,
});
export type ClockOutOverrideRequest = z.infer<typeof clockOutOverrideRequestSchemaShape>;
export const clockOutOverrideRequestSchema: z.ZodType<ClockOutOverrideRequest> =
  clockOutOverrideRequestSchemaShape;

export const submitClockOutOverrideBodySchema = z.object({
  employeeNote: z.string().trim().max(500).optional(),
});

export const decideClockOutOverrideBodySchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().max(500).optional(),
  })
  // A rejection without a reason leaves the employee blocked with no idea why the manager said no —
  // an approval needs no explanation the same way every other decide-route here leaves `remarks` optional.
  .refine((v) => v.decision !== 'reject' || (v.note && v.note.trim().length > 0), {
    message: 'A reason is required when rejecting',
    path: ['note'],
  });

export const listClockOutOverridesQuerySchema = paginationQuerySchema.extend({
  status: clockOutOverrideStatusSchema.optional(),
  /** `team` = the caller's direct reports (attendance:approve required), same scope shape every
   * other request queue in TeamApprovalsTab.tsx already uses. Omitted = the caller's own requests. */
  scope: z.enum(['team']).optional(),
  /** HR/manager view: one other specific person's requests (attendance:approve required).
   * Mutually exclusive with `scope` in practice, but both are independently validated. */
  personId: uuidSchema.optional(),
});

const clockOutPolicySchemaShape = z.object({
  orgId: orgIdSchema,
  /** `null` = the org-wide default row. */
  legalEntityId: legalEntityIdSchema.nullable(),
  enabled: z.boolean(),
  blockOnMissingEodReport: z.boolean(),
  blockOnOverdueTasks: z.boolean(),
  blockOnMandatoryLearning: z.boolean(),
  updatedAt: instantSchema.nullable(),
});
export type ClockOutPolicy = z.infer<typeof clockOutPolicySchemaShape>;
export const clockOutPolicySchema: z.ZodType<ClockOutPolicy> = clockOutPolicySchemaShape;

// ---------------------------------------------------------------------------
// Request/response schemas
// ---------------------------------------------------------------------------

export const getClockOutPolicyQuerySchema = z.object({
  /** Omitted = the org-default row; a value returns that legal entity's effective policy
   * (its own row, or the org default it inherits). */
  legalEntityId: legalEntityIdSchema.optional(),
});

export const updateClockOutPolicyBodySchema = z.object({
  legalEntityId: legalEntityIdSchema.nullable(),
  enabled: z.boolean(),
  blockOnMissingEodReport: z.boolean(),
  blockOnOverdueTasks: z.boolean(),
  blockOnMandatoryLearning: z.boolean(),
});

export const getClockOutReadinessQuerySchema = z.object({
  /** Defaults to today in the caller's own location timezone; only meaningful value in practice, but
   * exposed so the admin screen can preview another date's readiness. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const clockoutContract = c.router(
  {
    getPolicy: {
      method: 'GET',
      path: '/clock-out-policy',
      query: getClockOutPolicyQuerySchema,
      responses: { 200: clockOutPolicySchema, 400: errorBodySchema },
      metadata: routeMeta('clock_out_policy:manage'),
      summary: "Get this org's (or one legal entity's) effective clock-out policy",
    },
    updatePolicy: {
      method: 'PUT',
      path: '/clock-out-policy',
      body: updateClockOutPolicyBodySchema,
      responses: { 200: clockOutPolicySchema, 400: errorBodySchema },
      metadata: routeMeta('clock_out_policy:manage'),
      summary: 'Create or replace a clock-out policy row (org-default or one legal entity)',
    },
    getReadiness: {
      method: 'GET',
      path: '/clock-out-readiness',
      query: getClockOutReadinessQuerySchema,
      responses: { 200: clockOutReadinessSchema, 400: errorBodySchema },
      // Signed-in only: every employee may check their own readiness — there is nothing to
      // authorise beyond having a session, same as checking your own attendance status.
      metadata: routeMeta(null),
      summary: "Read the caller's own clock-out readiness for a date without attempting to punch",
    },

    // ---- Override / waiver requests (ADR 0020 §1) --------------------------------
    submitClockOutOverride: {
      method: 'POST',
      path: '/clock-out-override',
      body: submitClockOutOverrideBodySchema,
      responses: {
        201: clockOutOverrideRequestSchema,
        400: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      // Signed-in only: an employee requests an exception for their OWN blocked day.
      metadata: routeMeta(null),
      summary: "Ask a manager to clear today's clock-out block, submitting today's own reasons for review",
    },
    listClockOutOverrides: {
      method: 'GET',
      path: '/clock-out-override',
      query: listClockOutOverridesQuerySchema,
      responses: { 200: paginatedSchema(clockOutOverrideRequestSchema), 400: errorBodySchema },
      // Self scope (own requests) needs no permission; a `personId` for someone else is gated in
      // the handler on `attendance:approve`, the same permission that decides one.
      metadata: routeMeta(null),
      summary: "List override requests — the caller's own, or, with attendance:approve, another person's",
    },
    decideClockOutOverride: {
      method: 'POST',
      path: '/clock-out-override/:requestId/decide',
      pathParams: z.object({ requestId: uuidSchema }),
      body: decideClockOutOverrideBodySchema,
      responses: {
        200: clockOutOverrideRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      metadata: routeMeta('attendance:approve'),
      summary: 'Approve or reject a clock-out override request',
    },
  },
  { pathPrefix: '/attendance' },
);
